import { lazy } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import type { Session } from './_tools/Session';
import { checkUndefinedNullOrEmpty } from './_tools/Tools';
import EffizientClassesShowreel from './componentShowreel/EffizienzClasses';
import LoginRedirect from './login/LoginRedirect';
import LoginUWE from './login/LoginUWE';
import { LEAD } from './vars';

// Auth
const Logout = lazy(() => import('./login/Logout'));
const LoginController = lazy(() => import('./login/LoginController'));

// Dashboard
const Dashboard = lazy(() => import('./dashboard/Dashboard'));

// Leads
const LeadLayout = LEAD ? lazy(() => import('./leads/LeadLayout')) : undefined;
const AllLeads = LEAD ? lazy(() => import('./leads/liste/AllLeads')) : undefined;
const LeadNew = LEAD ? lazy(() => import('./leads/neu/LeadsNewAll')) : undefined;
const Statistik = LEAD ? lazy(() => import('./leads/statistic/Statistic')) : undefined;
const LeadEinzelansicht = LEAD ? lazy(() => import('./leads/einzelansicht/LeadEinzelansicht')) : undefined;

// Vehicle
const VehiclesLayout = LEAD ? lazy(() => import('./vehicles/VehiclesLayout')) : undefined;
const AllVehicles = LEAD ? lazy(() => import('./vehicles/liste/AllVehicles')) : undefined;
const VehiclesNew = LEAD ? lazy(() => import('./vehicles/neu/VehiclesNewAll')) : undefined;
const Fahrzeugbewertung = LEAD ? lazy(() => import('./vehicles/neu/bewertung/Bewertung')) : undefined;
const VehicleStatistics = LEAD ? lazy(() => import('./vehicles/statistic/VehicleStatistics')) : undefined;
const VehicleProtocol = LEAD ? lazy(() => import('./vehicles/proto/VehicleProto')) : undefined;
const VehicleEinzelansicht = LEAD ? lazy(() => import('./vehicles/einzelansicht/VehicleEinzelansicht')) : undefined;
const Zahlungsplan = LEAD ? lazy(() => import('./vehicles/einzelansicht/sold/Zahlungsplan')) : undefined;
const Verkaufsprovision = LEAD ? lazy(() => import('./vehicles/einzelansicht/sold/Verkaufsprovision')) : undefined;

// Kunden
const CustomerAll = lazy(() => import('./customer/KundenAll'));

// QR
const Inventur = LEAD ? lazy(() => import('./qr/inventur/Inventur')) : undefined;
const QRScanner = LEAD ? lazy(() => import('./qr/QRScanner')) : undefined;
const QRStatus = LEAD ? lazy(() => import('./qr/QRStatus')) : undefined;

// ShowReel
const BoxMaterialShowreel = lazy(() => import('./componentShowreel/BoxMaterial'));
const DataGridShowreel = lazy(() => import('./componentShowreel/DataGrid'));
const DialogBoxShowreel = lazy(() => import('./componentShowreel/DialogBox'));
const DnDShowreel = lazy(() => import('./componentShowreel/DnD'));
const FarbschemaShowreel = lazy(() => import('./componentShowreel/Farbschema'));
const InputFieldShowreel = lazy(() => import('./componentShowreel/InputField'));
const PortaleShowreel = lazy(() => import('./componentShowreel/Portale'));
const SiegelShowreel = lazy(() => import('./componentShowreel/Siegel'));
const SpeechbubbleShowreel = lazy(() => import('./componentShowreel/Speechbubble'));
const TableMaterialShowreel = lazy(() => import('./componentShowreel/TableMaterial'));
const TypographyShowreel = lazy(() => import('./componentShowreel/Typography'));

// Einstellungen
const EinstellungenBenutzer = lazy(() => import('./einstellungen/benutzer/Main'));
const EinstellungenBilder = lazy(() => import('./einstellungen/bilder/Main'));
const EinstellungenDruck = lazy(() => import('./einstellungen/druck/Main'));
const EventsAll = lazy(() => import('./einstellungen/events/Main'));
const EinstellungenFirma = lazy(() => import('./einstellungen/firma/Main'));
const MobileFreikontingente = lazy(() => import('./einstellungen/firma/MobileFreikontingente'));
const EinstellungenStandardwerte = lazy(() => import('./einstellungen/standardwerte/Main'));
const EinstellungenVertrage = lazy(() => import('./einstellungen/vertrage/Main'));

// Proto
const AllProto = lazy(() => import('./proto/AllProto'));

// Firmen
const AllFirmen = lazy(() => import('./support/firmen/AllFirmen'));
const AllFirmenVertrag = lazy(() => import('./support/firmen/vertrag/FirmenVertrag'));

// Logs
const ExportLog = lazy(() => import('./support/logs/ExportLog'));
const ErrorLog = lazy(() => import('./support/logs/ErrorLog'));

// Server
const Server = !LEAD ? lazy(() => import('./support/server/Server')) : undefined;

// Maps
const Maps = !LEAD ? lazy(() => import('./support/maps/Maps')) : undefined;

// Notifications
const Notifications = !LEAD ? lazy(() => import('./support/notifications/Notifications')) : undefined;

// GlobalSettings
const GlobalPSH = !LEAD ? lazy(() => import('./support/global-settings/psh')) : undefined;

// Ausstattung
const AusstattungZuordnung = !LEAD ? lazy(() => import('./einstellungen/ausstattungen/AusstattungZuordnung')) : undefined;
const AusstattungTexte = !LEAD ? lazy(() => import('./einstellungen/ausstattungen/AusstattungTexte')) : undefined;

const PATH_ROOT = '/';

export const PATH_AHDCARS = {
    root: PATH_ROOT,
    auth: {
        loginr: 'loginr',
        login: 'login',
        loginRedirect: {
            root: 'redirect',
            token: {
                root: ':token',
                redirect: ':redirect',
            },
        },
        loginUwe: 'offen',
        logout: 'logout',
    },
    dashboard: {
        root: 'dashboard',
    },
    leads: {
        root: 'leads',
        mode: ':mode',
        offen: 'offen',
        abgeschlossen: 'abgeschlossen',
        statistik: 'statistik',
        new: 'neu',
        lead: {
            root: 'lead',
            idLead: {
                root: ':idLead',
                mode: ':mode',
            },
        },
    },
    vehicles: {
        root: 'vehicles',
        bewertet: 'bewertet',
        geplant: 'geplant',
        bestand: 'bestand',
        verkauft: 'verkauft',
        neu: {
            root: 'neu',
            bewertung: 'neu/bewertung',
        },
        statistik: 'statistik',
        fahrzeug: {
            root: 'vehicle',
            idCar: {
                root: ':idCar',
                bilder: 'bilder',
                akte: 'akte',
                statistik: 'statistik',
                historie: 'historie',
                originalwerte: 'originalwerte',
                bewertung: 'bewertung',
                zahlungsplan: 'zahlungsplan',
                verkaufsprovision: 'verkaufsprovision',
                proto: 'proto',
            },
        },
    },
    kunden: {
        root: 'clients',
        aktKunde: ':aktKunde',
    },
    qr: {
        root: 'qr',
        inventur: 'inventur',
        scanner: 'scanner',
        code: {
            root: 'code',
            barcode: ':barcode',
        },
    },
    firmen: {
        root: 'firmen',
        vertrag: 'vertrag',
        client: {
            root: 'client',
            idFirma: {
                root: ':idFirma',
                mode: ':mode',
                idProto: ':idProto',
            },
        },
    },
    maps: {
        root: 'maps',
    },
    logs: {
        root: 'logs',
        export: 'export',
        error: 'error',
    },
    server: {
        root: 'server',
    },
    notifications: {
        root: 'notifications',
    },
    globalSettings: {
        root: 'global-settings',
        psh: 'psh',
    },
    ausstattung: {
        root: 'ausstattung',
        texte: 'texte',
        zuordnungen: 'zuordnungen',
    },
    showreel: {
        root: 'showreel',
        boxmaterial: 'boxmaterial',
        dialogbox: 'dialogbox',
        inputfield: 'inputfield',
        typography: 'typography',
        farbschema: 'farbschema',
        portale: 'portale',
        tablematerial: 'tablematerial',
        speechbubble: 'speechbubble',
        dnd: 'dnd',
        datagrid: 'datagrid',
        effizienzClasses: 'effizienz',
        siegel: 'siegel',
    },
    einstellungen: {
        root: 'einstellungen',
        ausstattungen: {
            root: 'ausstattungen',
            error: {
                root: 'error',
                id: ':id',
            },
        },
        benutzer: 'benutzer',
        bilder: 'bilder',
        druck: 'druck',
        events: 'events',
        firma: 'firma',
        freikontingente: 'freikontingente',
        standardwerte: 'standardwerte',
        vertrage: 'vertrage',
    },
    proto: {
        root: 'proto',
        mode: {
            root: ':mode',
            idProto: ':idProto',
        },
    },
};

export const routesAhdCars = (session: Session): RouteObject[] => {
    const navigateToRoot = <Navigate replace to={'/' + (!checkUndefinedNullOrEmpty(session) ? PATH_AHDCARS.dashboard.root : PATH_AHDCARS.auth.loginr)} />;

    const isAdminSupportOrVKL = session?.gIsAdmin || session?.gIsSupport || session?.gIsVKL;

    return [
        { path: PATH_AHDCARS.dashboard.root, element: <Dashboard /> },
        {
            path: PATH_AHDCARS.einstellungen.root,
            element: isAdminSupportOrVKL == true ? <Outlet /> : navigateToRoot,
            children: [
                { path: PATH_AHDCARS.einstellungen.benutzer, element: isAdminSupportOrVKL == true ? <EinstellungenBenutzer /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.bilder, element: isAdminSupportOrVKL == true ? <EinstellungenBilder /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.druck, element: isAdminSupportOrVKL == true ? <EinstellungenDruck /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.events, element: isAdminSupportOrVKL == true ? <EventsAll /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.firma, element: isAdminSupportOrVKL == true ? <EinstellungenFirma /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.freikontingente, element: isAdminSupportOrVKL == true ? <MobileFreikontingente /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.standardwerte, element: isAdminSupportOrVKL == true ? <EinstellungenStandardwerte /> : navigateToRoot },
                { path: PATH_AHDCARS.einstellungen.vertrage, element: isAdminSupportOrVKL == true ? <EinstellungenVertrage /> : navigateToRoot },
                {
                    path: '*',
                    index: true,
                    element: isAdminSupportOrVKL == true ? <Navigate replace to={'/' + PATH_AHDCARS.einstellungen.root + '/' + PATH_AHDCARS.einstellungen.firma} /> : navigateToRoot,
                },
            ],
        },
        {
            path: PATH_AHDCARS.proto.root,
            element: <AllProto />,
            children: [
                {
                    path: PATH_AHDCARS.proto.mode.root,
                    element: <Outlet />,
                    children: [{ path: PATH_AHDCARS.proto.mode.idProto, element: <Outlet /> }],
                },
            ],
        },
        { path: PATH_AHDCARS.auth.logout, element: <Logout /> },
        { path: PATH_AHDCARS.auth.loginr, element: <LoginRedirect /> },
        { path: PATH_AHDCARS.auth.login, element: <LoginController /> },
        { path: PATH_AHDCARS.auth.loginUwe, element: <LoginUWE /> },
        {
            path: PATH_AHDCARS.auth.loginRedirect.root,
            element: <LoginRedirect />,
            children: [
                {
                    path: PATH_AHDCARS.auth.loginRedirect.token.root,
                    element: <LoginRedirect />,
                    children: [{ path: PATH_AHDCARS.auth.loginRedirect.token.redirect, element: <LoginRedirect /> }],
                },
            ],
        },
        {
            path: PATH_AHDCARS.showreel.root,
            element: <Outlet />,
            children: [
                { path: PATH_AHDCARS.showreel.boxmaterial, element: <BoxMaterialShowreel /> },
                { path: PATH_AHDCARS.showreel.dialogbox, element: <DialogBoxShowreel /> },
                { path: PATH_AHDCARS.showreel.inputfield, element: <InputFieldShowreel /> },
                { path: PATH_AHDCARS.showreel.typography, element: <TypographyShowreel /> },
                { path: PATH_AHDCARS.showreel.farbschema, element: <FarbschemaShowreel /> },
                { path: PATH_AHDCARS.showreel.portale, element: <PortaleShowreel /> },
                { path: PATH_AHDCARS.showreel.tablematerial, element: <TableMaterialShowreel /> },
                { path: PATH_AHDCARS.showreel.speechbubble, element: <SpeechbubbleShowreel /> },
                { path: PATH_AHDCARS.showreel.dnd, element: <DnDShowreel /> },
                { path: PATH_AHDCARS.showreel.datagrid, element: <DataGridShowreel /> },
                { path: PATH_AHDCARS.showreel.effizienzClasses, element: <EffizientClassesShowreel /> },
                { path: PATH_AHDCARS.showreel.siegel, element: <SiegelShowreel /> },
                { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.showreel.root + '/' + PATH_AHDCARS.showreel.boxmaterial} /> },
            ],
        },
        ...(LEAD
            ? [
                  {
                      path: PATH_AHDCARS.leads.root,
                      element: <LeadLayout />,
                      children: [
                          { path: PATH_AHDCARS.leads.new, element: <LeadNew /> },
                          { path: PATH_AHDCARS.leads.statistik, element: isAdminSupportOrVKL === true ? <Statistik /> : navigateToRoot },
                          {
                              path: PATH_AHDCARS.leads.lead.root,
                              element: <Outlet />,
                              children: [
                                  {
                                      path: PATH_AHDCARS.leads.lead.idLead.root,
                                      element: <LeadEinzelansicht />,
                                      children: [{ path: PATH_AHDCARS.leads.lead.idLead.mode, element: <LeadEinzelansicht /> }],
                                  },
                              ],
                          },
                          { path: PATH_AHDCARS.leads.mode, element: <AllLeads /> },
                          { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.leads.root + '/' + PATH_AHDCARS.leads.offen} /> },
                      ],
                  },
                  {
                      path: PATH_AHDCARS.vehicles.root,
                      element: <VehiclesLayout />,
                      children: [
                          { path: PATH_AHDCARS.vehicles.bewertet, element: <AllVehicles /> },
                          { path: PATH_AHDCARS.vehicles.geplant, element: <AllVehicles /> },
                          { path: PATH_AHDCARS.vehicles.bestand, element: <AllVehicles /> },
                          { path: PATH_AHDCARS.vehicles.verkauft, element: <AllVehicles /> },
                          { path: PATH_AHDCARS.vehicles.neu.root, element: <VehiclesNew /> },
                          { path: PATH_AHDCARS.vehicles.neu.bewertung, element: <Fahrzeugbewertung /> },
                          { path: PATH_AHDCARS.vehicles.statistik, element: <VehicleStatistics /> },
                          {
                              path: PATH_AHDCARS.vehicles.fahrzeug.root,
                              element: <Outlet />,
                              children: [
                                  {
                                      path: PATH_AHDCARS.vehicles.fahrzeug.idCar.root,
                                      element: <Outlet />,
                                      children: [
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.bewertung, element: <Fahrzeugbewertung /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.zahlungsplan, element: <Zahlungsplan /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.verkaufsprovision, element: <Verkaufsprovision /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.proto, element: <VehicleProtocol /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.bilder, element: <VehicleEinzelansicht /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.akte, element: <VehicleEinzelansicht /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.statistik, element: <VehicleEinzelansicht /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.historie, element: <VehicleEinzelansicht /> },
                                          { path: PATH_AHDCARS.vehicles.fahrzeug.idCar.originalwerte, element: <VehicleEinzelansicht /> },
                                          { index: true, element: <VehicleEinzelansicht /> },
                                      ],
                                  },
                              ],
                          },
                          { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.vehicles.root + '/' + PATH_AHDCARS.vehicles.bestand} /> },
                      ],
                  },
                  {
                      path: PATH_AHDCARS.kunden.root,
                      element: <CustomerAll />,
                      children: [{ path: PATH_AHDCARS.kunden.aktKunde, element: <CustomerAll /> }],
                  },
                  {
                      path: PATH_AHDCARS.qr.root,
                      element: <Outlet />,
                      children: [
                          { path: PATH_AHDCARS.qr.inventur, element: <Inventur /> },
                          { path: PATH_AHDCARS.qr.scanner, element: <QRScanner /> },
                          {
                              path: PATH_AHDCARS.qr.code.root,
                              element: <Outlet />,
                              children: [{ path: PATH_AHDCARS.qr.code.barcode, element: <QRStatus /> }],
                          },
                          { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.qr.root + '/' + PATH_AHDCARS.qr.scanner} /> },
                      ],
                  },
              ]
            : [
                  {
                      path: PATH_AHDCARS.firmen.root,
                      element: <Outlet />,
                      children: [
                          { path: PATH_AHDCARS.firmen.vertrag, element: <AllFirmenVertrag /> },
                          {
                              path: '*',
                              element: <AllFirmen />,
                              children: [
                                  {
                                      path: PATH_AHDCARS.firmen.client.root,
                                      element: <Outlet />,
                                      children: [
                                          {
                                              path: PATH_AHDCARS.firmen.client.idFirma.root,
                                              element: <Outlet />,
                                              children: [
                                                  {
                                                      path: PATH_AHDCARS.firmen.client.idFirma.mode,
                                                      element: <Outlet />,
                                                      children: [{ path: PATH_AHDCARS.firmen.client.idFirma.idProto, element: <Outlet /> }],
                                                  },
                                              ],
                                          },
                                      ],
                                  },
                                  { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.firmen.root} /> },
                              ],
                          },
                          { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.firmen.root + '/' + PATH_AHDCARS.firmen.client.root} /> },
                      ],
                  },
                  { path: PATH_AHDCARS.maps.root, element: <Maps /> },
                  {
                      path: PATH_AHDCARS.logs.root,
                      element: <Outlet />,
                      children: [
                          { path: PATH_AHDCARS.logs.export, element: <ExportLog /> },
                          { path: PATH_AHDCARS.logs.error, element: <ErrorLog /> },
                          { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.logs.root + '/' + PATH_AHDCARS.logs.export} /> },
                      ],
                  },
                  {
                      path: PATH_AHDCARS.ausstattung.root,
                      element: <Outlet />,
                      children: [
                          { path: PATH_AHDCARS.ausstattung.zuordnungen, element: <AusstattungZuordnung /> },
                          { path: PATH_AHDCARS.ausstattung.texte, element: <AusstattungTexte /> },
                          { path: '*', index: true, element: <Navigate replace to={'/' + PATH_AHDCARS.ausstattung.root + '/' + PATH_AHDCARS.ausstattung.zuordnungen} /> },
                      ],
                  },
                  { path: PATH_AHDCARS.server.root, element: <Server /> },
                  { path: PATH_AHDCARS.notifications.root, element: <Notifications /> },
                  {
                      path: PATH_AHDCARS.globalSettings.root,
                      element: isAdminSupportOrVKL == true ? <Outlet /> : navigateToRoot,
                      children: [
                          { path: PATH_AHDCARS.globalSettings.psh, element: <GlobalPSH /> },
                          {
                              path: '*',
                              index: true,
                              element: <Navigate replace to={'/' + PATH_AHDCARS.globalSettings.root + '/' + PATH_AHDCARS.globalSettings.psh} />,
                          },
                      ],
                  },
              ]),
        {
            path: '*',
            index: true,
            element: navigateToRoot,
        },
    ];
};
